import { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Bug, X, Send, CheckCircle2, AlertTriangle, Camera, Loader2 } from 'lucide-react';

/**
 * Floating "Report an issue" button + modal.
 * Visible on every page to every authenticated user. Captures:
 *  - title (required)
 *  - description
 *  - category (bug / ux / data / performance / feature_request / question / other)
 *  - severity (low / medium / high / critical)
 *  - screenshot (optional, up to 5MB)
 *  - auto: page_url, user_agent, user identity
 */
export default function IssueReporter() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'bug',
    severity: 'medium',
  });
  const [screenshot, setScreenshot] = useState(null);
  const fileRef = useRef(null);

  if (!user) return null;

  const reset = () => {
    setForm({ title: '', description: '', category: 'bug', severity: 'medium' });
    setScreenshot(null);
    setSubmitted(false);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleOpen = () => { reset(); setOpen(true); };
  const handleClose = () => { setOpen(false); setTimeout(reset, 400); };

  const submit = async () => {
    if (!form.title.trim()) { setError('Please add a short title'); return; }
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('category', form.category);
      fd.append('severity', form.severity);
      fd.append('page_url', typeof window !== 'undefined' ? window.location.href : location.pathname);
      fd.append('user_agent', typeof navigator !== 'undefined' ? navigator.userAgent : '');
      if (screenshot) fd.append('screenshot', screenshot);
      await api.post('/issues', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
      setTimeout(() => handleClose(), 2200);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={handleOpen}
        title="Report an issue"
        data-testid="report-issue-fab"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-1.5 px-3.5 py-2.5 rounded-full shadow-lg hover:shadow-xl transition-all text-xs font-semibold text-white"
        style={{ background: 'linear-gradient(135deg, #D4726A 0%, #BA3F3F 100%)' }}
      >
        <Bug size={14} />
        <span className="hidden sm:inline">Report an issue</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.55)' }} onClick={handleClose} data-testid="issue-reporter-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} style={{ border: '1px solid #E5E1DC' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#F3F4F6' }}>
              <div className="flex items-center gap-2">
                <Bug size={16} style={{ color: '#D4726A' }} />
                <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Report an issue</h3>
              </div>
              <button onClick={handleClose} className="p-1 rounded hover:bg-gray-100" data-testid="issue-close">
                <X size={16} style={{ color: '#6B7280' }} />
              </button>
            </div>

            {submitted ? (
              <div className="p-8 text-center">
                <CheckCircle2 size={44} style={{ color: '#2D8B5E' }} className="mx-auto mb-3" />
                <p className="text-base font-bold" style={{ color: '#1B2A4A' }}>Thank you!</p>
                <p className="text-xs mt-1.5" style={{ color: '#6B7280' }}>
                  Your issue has been logged. Dipali will review and respond shortly.
                </p>
              </div>
            ) : (
              <div className="p-5 space-y-3">
                <p className="text-[12px]" style={{ color: '#6B7280' }}>
                  Tell us what's broken or confusing on this page. We'll see it instantly and get back to you.
                </p>

                {/* Title */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Title <span style={{ color: '#D4726A' }}>*</span></label>
                  <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g., Godrej payment shows 6L but nothing has been collected"
                    className="mt-1 w-full text-sm border rounded px-3 py-2" style={{ borderColor: '#E5E1DC' }}
                    data-testid="issue-title" maxLength={200} />
                </div>

                {/* Category + Severity */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Type</label>
                    <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                      className="mt-1 w-full text-xs border rounded px-2 py-2" style={{ borderColor: '#E5E1DC' }} data-testid="issue-category">
                      <option value="bug">🐛 Bug — something is broken</option>
                      <option value="data">📊 Wrong data / number</option>
                      <option value="ux">🎨 UX — confusing or hard to find</option>
                      <option value="performance">⚡ Slow / laggy</option>
                      <option value="feature_request">💡 Feature request</option>
                      <option value="question">❓ Question / how-to</option>
                      <option value="other">🗒 Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Severity</label>
                    <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                      className="mt-1 w-full text-xs border rounded px-2 py-2" style={{ borderColor: '#E5E1DC' }} data-testid="issue-severity">
                      <option value="low">Low — minor</option>
                      <option value="medium">Medium — annoying</option>
                      <option value="high">High — blocks work</option>
                      <option value="critical">Critical — data loss / down</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Details</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={4} maxLength={4000}
                    placeholder="What were you trying to do? What happened instead? What did you expect?"
                    className="mt-1 w-full text-sm border rounded px-3 py-2 resize-none" style={{ borderColor: '#E5E1DC' }}
                    data-testid="issue-description" />
                </div>

                {/* Screenshot */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Screenshot (optional, up to 5MB)</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input ref={fileRef} type="file" accept="image/*"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f && f.size > 5 * 1024 * 1024) { setError('Screenshot must be under 5MB'); return; }
                        setScreenshot(f || null);
                        setError('');
                      }}
                      className="text-[11px]" data-testid="issue-screenshot" />
                    {screenshot && (
                      <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#2D8B5E' }}>
                        <Camera size={11} /> {screenshot.name} · {(screenshot.size / 1024).toFixed(0)} KB
                      </span>
                    )}
                  </div>
                </div>

                {/* Context summary */}
                <div className="rounded-md p-2.5 text-[11px]" style={{ backgroundColor: '#F9FAFB', color: '#6B7280' }}>
                  <p>We'll also attach: current page URL, browser info, and your identity ({user.email}).</p>
                </div>

                {error && (
                  <div className="flex items-center gap-1.5 rounded-md px-2.5 py-2 text-[11px]" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }} data-testid="issue-error">
                    <AlertTriangle size={12} /> {error}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button onClick={handleClose} className="px-3 py-2 text-xs font-medium rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="issue-cancel">Cancel</button>
                  <button onClick={submit} disabled={submitting || !form.title.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded text-white disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #D4726A 0%, #BA3F3F 100%)' }}
                    data-testid="issue-submit">
                    {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    {submitting ? 'Submitting…' : 'Submit report'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
